import React from 'react';
import { Card, CardBody } from './BaseComponents';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
}

/**
 * BaseModal - A reusable modal component foundation
 *
 * Provides consistent modal behavior and styling including:
 * - Full-screen overlay with backdrop
 * - Centered content container
 * - Optional close button
 * - Click-outside-to-close behavior
 * - Proper accessibility and focus management
 * - Consistent shadow and spacing
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  className = "max-w-md w-full shadow-large",
  showCloseButton = true,
  closeOnOverlayClick = true
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="modal-overlay overflow-y-auto overflow-x-hidden"
      onClick={handleOverlayClick}
    >
      <div className="flex items-center justify-center min-h-full p-3 sm:p-4">
        <div onClick={handleContentClick} className={className}>
          <Card className="p-4 sm:p-6 w-full" noHover={true}>
            {showCloseButton && (
              <div className="relative">
                <button
                  onClick={onClose}
                  className="absolute top-2 right-1 text-neutral-400 hover:text-neutral-600 transition-colors duration-200 z-10"
                  aria-label="Close modal"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <CardBody>
              {children}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};